import React, { useState, useEffect, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
  getData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  Trans_TypeID: 0,
  Trans_TypeName: "",
  Trans_Status: "Y",
};

const TransTypeForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleBack = () => {
    window.location.href = "/transType";
  };

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        Trans_TypeID: selectedData.Trans_TypeID,
        Trans_TypeName: selectedData.Trans_TypeName || "",
        Trans_Status: selectedData.Trans_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const existing = await getData(
        "Tb_Set_TransType",
        "Trans_Status = 'Y'"
      );

      const newName = (formData.Trans_TypeName || "").trim().toLowerCase();
      const isDuplicate = existing.some(
        (r) => (r.Trans_TypeName || "").trim().toLowerCase() === newName
      );

      if (isDuplicate) {
        getAlert("FAILED", "ชื่อวิธีการขนส่งนี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const { Trans_TypeID, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);

      await insertData("Tb_Set_TransType", fields, values);

      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error in handleInsert:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const existing = await getData(
        "Tb_Set_TransType",
        "Trans_Status = 'Y'"
      );

      const newName = (formData.Trans_TypeName || "").trim().toLowerCase();
      const isDuplicate = existing.some(
        (r) =>
          (r.Trans_TypeName || "").trim().toLowerCase() === newName &&
          r.Trans_TypeID !== formData.Trans_TypeID
      );

      if (isDuplicate) {
        getAlert("FAILED", "ชื่อวิธีการขนส่งนี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const where = { Trans_TypeID: formData.Trans_TypeID };
      const { Trans_TypeID, ...values } = formData;
      const res = await updateData("Tb_Set_TransType", values, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.Trans_TypeID !== undefined) {
          await deleteData(
            "Tb_Set_TransType",
            `WHERE Trans_TypeID = ${selectedData.Trans_TypeID}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Trans_TypeName) {
      getAlert("WARNING", "โปรดกรอกชื่อวิธีการขนส่ง");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.Trans_TypeName, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/transType"
      mode={mode}
      onSubmit={handleSubmit}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-shipping-fast" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>วิธีการขนส่ง</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-6">
          <label style={labelStyle}>วิธีการขนส่ง</label>
          <input
            type="text"
            className="form-control"
            name="Trans_TypeName"
            value={formData.Trans_TypeName || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-6">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="Trans_Status"
            value={formData.Trans_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default TransTypeForm;
